import React, { useMemo, useState } from 'react';
import axiosInstance from '../utils/axiosInstance';
import './css/CommanderOrphanAdminView.css';

const numberFmt = (value) => Number(value || 0).toLocaleString();

const sampleTypeLabels = {
  participantResults: 'Participant Results',
  strongholdDefenders: 'Stronghold Defenders',
  battleAttackers: 'Battle Attackers',
  battleDefenders: 'Battle Defenders',
  commanderHistory: 'Commander History',
};

const orphanTypeLabels = {
  event_only: 'Event-linked',
  history_only: 'History-only',
  mixed: 'Mixed',
};

const CommanderOrphanAdminView = ({ compact = false }) => {
  const [nationId, setNationId] = useState('');
  const [sampleLimitPerType, setSampleLimitPerType] = useState(40);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [scanResult, setScanResult] = useState(null);
  const [targetBySourceId, setTargetBySourceId] = useState({});
  const [rowBusyBySourceId, setRowBusyBySourceId] = useState({});
  const [stitchFeedbackBySourceId, setStitchFeedbackBySourceId] = useState({});
  const [orphanTypeFilter, setOrphanTypeFilter] = useState('all');

  const orphanGroups = useMemo(() => (Array.isArray(scanResult?.orphanGroups) ? scanResult.orphanGroups : []), [scanResult]);

  const visibleOrphanGroups = useMemo(() => {
    if (orphanTypeFilter === 'all') return orphanGroups;
    return orphanGroups.filter((group) => String(group?.orphanType || '') === orphanTypeFilter);
  }, [orphanGroups, orphanTypeFilter]);

  const runScan = async () => {
    setIsLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (String(nationId || '').trim()) params.set('nationId', String(nationId).trim());
      params.set('sampleLimitPerType', String(sampleLimitPerType || 40));

      const response = await axiosInstance.get(`/commanders/orphans?${params.toString()}`);
      setScanResult(response?.data || null);
    } catch (scanError) {
      console.error('Error scanning orphan commander references:', scanError);
      setError(scanError?.response?.data?.message || 'Failed to scan orphan commander references.');
    } finally {
      setIsLoading(false);
    }
  };

  const runRecreate = async (group) => {
    const sourceCommanderId = String(group?.orphanCommanderId || '').trim();
    if (!sourceCommanderId) return;

    const resolvedNationId = String(nationId || group?.nationIds?.[0] || '').trim();
    if (!resolvedNationId) {
      setStitchFeedbackBySourceId((prev) => ({
        ...prev,
        [sourceCommanderId]: {
          ok: false,
          message: 'Cannot recreate: nationId is required (set filter or ensure group has nation).',
        },
      }));
      return;
    }

    setRowBusyBySourceId((prev) => ({ ...prev, [sourceCommanderId]: true }));
    setStitchFeedbackBySourceId((prev) => ({ ...prev, [sourceCommanderId]: null }));

    try {
      const payload = {
        sourceCommanderId,
        nationId: resolvedNationId,
        name: Array.isArray(group?.nameHints) && group.nameHints[0] ? String(group.nameHints[0]).trim() : undefined,
      };
      const response = await axiosInstance.post('/commanders/orphans/recreate', payload);
      const data = response?.data || {};

      setTargetBySourceId((prev) => ({
        ...prev,
        [sourceCommanderId]: sourceCommanderId,
      }));

      setStitchFeedbackBySourceId((prev) => ({
        ...prev,
        [sourceCommanderId]: {
          ok: true,
          message: data?.existed
            ? `Commander record already exists${data?.commander?.name ? ` (${data.commander.name})` : ''}. Target set.`
            : `Recreated commander${data?.commander?.name ? ` (${data.commander.name})` : ''}. Target set.`,
          payload: data,
        },
      }));

      await runScan();
    } catch (recreateError) {
      console.error('Error recreating orphan commander row:', recreateError);
      setStitchFeedbackBySourceId((prev) => ({
        ...prev,
        [sourceCommanderId]: {
          ok: false,
          message: recreateError?.response?.data?.message || 'Failed to recreate orphan commander row.',
        },
      }));
    } finally {
      setRowBusyBySourceId((prev) => ({ ...prev, [sourceCommanderId]: false }));
    }
  };

  const runDeleteHistory = async (group, { dryRun = false } = {}) => {
    const sourceCommanderId = String(group?.orphanCommanderId || '').trim();
    if (!sourceCommanderId) return;

    const resolvedNationId = String(nationId || group?.nationIds?.[0] || '').trim();
    if (!resolvedNationId) {
      setStitchFeedbackBySourceId((prev) => ({
        ...prev,
        [sourceCommanderId]: {
          ok: false,
          message: 'Cannot delete history: nationId is required (set filter or ensure group has nation).',
        },
      }));
      return;
    }

    if (!dryRun) {
      const confirmed = window.confirm(`Delete commander history rows for orphan ${sourceCommanderId}? This cannot be undone.`);
      if (!confirmed) return;
    }

    setRowBusyBySourceId((prev) => ({ ...prev, [sourceCommanderId]: true }));
    setStitchFeedbackBySourceId((prev) => ({ ...prev, [sourceCommanderId]: null }));

    try {
      const response = await axiosInstance.post('/commanders/orphans/history-delete', {
        sourceCommanderId,
        nationId: resolvedNationId,
        dryRun,
      });
      const data = response?.data || {};
      setStitchFeedbackBySourceId((prev) => ({
        ...prev,
        [sourceCommanderId]: {
          ok: true,
          message: dryRun
            ? `History delete dry run · matched docs: ${numberFmt(data?.matchedHistoryDocs)}`
            : `Deleted history docs: ${numberFmt(data?.deletedHistoryDocs)}`,
          payload: data,
        },
      }));

      if (!dryRun) await runScan();
    } catch (deleteError) {
      console.error('Error deleting orphan commander history rows:', deleteError);
      setStitchFeedbackBySourceId((prev) => ({
        ...prev,
        [sourceCommanderId]: {
          ok: false,
          message: deleteError?.response?.data?.message || 'Failed to delete orphan commander history rows.',
        },
      }));
    } finally {
      setRowBusyBySourceId((prev) => ({ ...prev, [sourceCommanderId]: false }));
    }
  };

  const runStitch = async (sourceCommanderId, { dryRun = false } = {}) => {
    const targetCommanderId = String(targetBySourceId[sourceCommanderId] || '').trim();
    if (!sourceCommanderId || !targetCommanderId) {
      setStitchFeedbackBySourceId((prev) => ({
        ...prev,
        [sourceCommanderId]: {
          ok: false,
          message: 'Target commander ID is required.',
        },
      }));
      return;
    }

    setRowBusyBySourceId((prev) => ({ ...prev, [sourceCommanderId]: true }));
    setStitchFeedbackBySourceId((prev) => ({ ...prev, [sourceCommanderId]: null }));

    try {
      const payload = {
        sourceCommanderId,
        targetCommanderId,
        dryRun,
      };
      if (String(nationId || '').trim()) payload.nationId = String(nationId).trim();

      const response = await axiosInstance.post('/commanders/orphans/stitch', payload);
      const data = response?.data || {};
      setStitchFeedbackBySourceId((prev) => ({
        ...prev,
        [sourceCommanderId]: {
          ok: true,
          message: dryRun
            ? `Dry run complete · matched docs: ${numberFmt(Object.values(data?.matches || data?.matchedDocs || {}).reduce((sum, val) => sum + Number(val || 0), 0))}`
            : `Stitched successfully · updated docs: ${numberFmt(Object.values(data?.stitched || {}).reduce((sum, val) => sum + Number(val || 0), 0))}`,
          payload: data,
        },
      }));

      if (!dryRun) {
        await runScan();
      }
    } catch (stitchError) {
      console.error('Error stitching orphan references:', stitchError);
      setStitchFeedbackBySourceId((prev) => ({
        ...prev,
        [sourceCommanderId]: {
          ok: false,
          message: stitchError?.response?.data?.message || 'Failed to stitch orphan references.',
        },
      }));
    } finally {
      setRowBusyBySourceId((prev) => ({ ...prev, [sourceCommanderId]: false }));
    }
  };

  return (
    <div className={`orphan-admin-panel ${compact ? 'compact' : ''}`}>
      <div className="orphan-admin-header">
        <div>
          <h3>Commander Orphan Admin</h3>
          <p>Scan orphan references in event records + commander history, segment by type, then stitch to valid commanders.</p>
        </div>
        <div className="orphan-admin-actions">
          <button type="button" onClick={runScan} disabled={isLoading}>
            {isLoading ? 'Scanning…' : 'Scan Orphans'}
          </button>
        </div>
      </div>

      <div className="orphan-admin-filters">
        <label>
          Nation ID (optional)
          <input
            type="text"
            value={nationId}
            onChange={(e) => setNationId(e.target.value)}
            placeholder="Leave blank for all (superUser)"
          />
        </label>
        <label>
          Sample limit per type
          <input
            type="number"
            min={1}
            max={200}
            value={sampleLimitPerType}
            onChange={(e) => setSampleLimitPerType(Math.max(1, Math.min(200, Number(e.target.value || 40))))}
          />
        </label>
      </div>

      {error ? <div className="orphan-admin-error">{error}</div> : null}

      {scanResult?.summary ? (
        <div className="orphan-admin-summary-grid">
          <div><span>Orphan Commanders</span><strong>{numberFmt(scanResult.summary.orphanCommanderCount)}</strong></div>
          <div><span>Total Orphan Refs</span><strong>{numberFmt(scanResult.summary.total)}</strong></div>
          <div><span>Event-linked Refs</span><strong>{numberFmt(scanResult.summary.eventReferences)}</strong></div>
          <div><span>History Refs</span><strong>{numberFmt(scanResult.summary.historyReferences)}</strong></div>
          <div><span>Event-only IDs</span><strong>{numberFmt(scanResult.summary.eventOnlyOrphanCommanderCount)}</strong></div>
          <div><span>History-only IDs</span><strong>{numberFmt(scanResult.summary.historyOnlyOrphanCommanderCount)}</strong></div>
          <div><span>Mixed IDs</span><strong>{numberFmt(scanResult.summary.mixedOrphanCommanderCount)}</strong></div>
        </div>
      ) : null}

      <div className="orphan-admin-type-filters" role="tablist" aria-label="Orphan type filters">
        <button
          type="button"
          className={orphanTypeFilter === 'all' ? 'active' : ''}
          onClick={() => setOrphanTypeFilter('all')}
        >
          All
        </button>
        <button
          type="button"
          className={orphanTypeFilter === 'event_only' ? 'active' : ''}
          onClick={() => setOrphanTypeFilter('event_only')}
        >
          Event-linked
        </button>
        <button
          type="button"
          className={orphanTypeFilter === 'history_only' ? 'active' : ''}
          onClick={() => setOrphanTypeFilter('history_only')}
        >
          History-only
        </button>
        <button
          type="button"
          className={orphanTypeFilter === 'mixed' ? 'active' : ''}
          onClick={() => setOrphanTypeFilter('mixed')}
        >
          Mixed
        </button>
      </div>

      {!visibleOrphanGroups.length ? (
        <div className="orphan-admin-empty">Run a scan to see orphan groups for this filter.</div>
      ) : (
        <div className="orphan-admin-table-wrap">
          <table className="orphan-admin-table">
            <thead>
              <tr>
                <th>Orphan Commander ID</th>
                <th>Type</th>
                <th>Total</th>
                <th>Event Refs</th>
                <th>History Refs</th>
                <th>Name Hints</th>
                <th>Suggested Targets</th>
                <th>Samples</th>
                <th>Stitch Target</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleOrphanGroups.map((group) => {
                const sourceId = String(group?.orphanCommanderId || '');
                const counts = group?.counts || {};
                const feedback = stitchFeedbackBySourceId[sourceId];
                const isBusy = !!rowBusyBySourceId[sourceId];
                const nameHints = Array.isArray(group?.nameHints) ? group.nameHints : [];
                const targetCandidates = Array.isArray(group?.targetCandidates) ? group.targetCandidates : [];

                return (
                  <tr key={sourceId}>
                    <td className="orphan-id-cell"><code>{sourceId}</code></td>
                    <td>{orphanTypeLabels[group?.orphanType] || 'Unknown'}</td>
                    <td>{numberFmt(counts.total)}</td>
                    <td>{numberFmt(group?.eventReferenceCount)}</td>
                    <td>{numberFmt(group?.historyReferenceCount)}</td>
                    <td>
                      {nameHints.length ? (
                        <div className="orphan-name-hints">
                          {nameHints.map((hint) => (
                            <span key={`${sourceId}-${hint}`} className="orphan-hint-chip">{hint}</span>
                          ))}
                        </div>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td>
                      {targetCandidates.length ? (
                        <div className="orphan-target-candidates">
                          {targetCandidates.map((candidate) => (
                            <button
                              type="button"
                              key={`${sourceId}-${candidate.commanderId}`}
                              onClick={() => setTargetBySourceId((prev) => ({
                                ...prev,
                                [sourceId]: String(candidate.commanderId),
                              }))}
                            >
                              {candidate.name} · N{candidate.nationId ? candidate.nationId.slice(-4) : '—'}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <span className="muted">No exact name matches</span>
                      )}
                    </td>
                    <td>
                      <details>
                        <summary>View samples</summary>
                        <div className="orphan-samples-list">
                          {Object.entries(sampleTypeLabels).map(([sampleKey, label]) => {
                            const rows = Array.isArray(group?.samples?.[sampleKey]) ? group.samples[sampleKey] : [];
                            if (!rows.length) return null;
                            return (
                              <div key={`${sourceId}-${sampleKey}`} className="orphan-sample-section">
                                <strong>{label} ({rows.length})</strong>
                                <ul>
                                  {rows.map((row, idx) => (
                                    <li key={`${sourceId}-${sampleKey}-${idx}`}>
                                      <code>{JSON.stringify(row)}</code>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            );
                          })}
                        </div>
                      </details>
                    </td>
                    <td>
                      <input
                        type="text"
                        value={targetBySourceId[sourceId] || ''}
                        onChange={(e) => setTargetBySourceId((prev) => ({
                          ...prev,
                          [sourceId]: e.target.value,
                        }))}
                        placeholder="Target commander ID"
                      />
                    </td>
                    <td>
                      <div className="orphan-row-actions">
                        <button type="button" onClick={() => runRecreate(group)} disabled={isBusy}>
                          Recreate Record
                        </button>
                        <button type="button" onClick={() => runDeleteHistory(group, { dryRun: true })} disabled={isBusy}>
                          History Dry Run
                        </button>
                        <button type="button" onClick={() => runDeleteHistory(group, { dryRun: false })} disabled={isBusy}>
                          Delete History
                        </button>
                        <button type="button" onClick={() => runStitch(sourceId, { dryRun: true })} disabled={isBusy}>
                          Stitch Dry Run
                        </button>
                        <button type="button" className="primary" onClick={() => runStitch(sourceId, { dryRun: false })} disabled={isBusy}>
                          Stitch
                        </button>
                      </div>
                      {feedback?.message ? (
                        <div className={`orphan-row-feedback ${feedback.ok ? 'ok' : 'error'}`}>{feedback.message}</div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CommanderOrphanAdminView;
